import { produce } from 'immer';
import { head, findIndex, isNil, inRange } from 'lodash/fp';
import { AggregationType } from 'app/lib/constants';
import { isNumericType, availableAggregations } from 'app/lib/utils/summarize';
import {
    ADD_OPERATION_TO_STACK,
    CANCEL_OPERATION,
    CREATE_TRANSFORMATION,
    UPDATE_TRANSFORM_OPERATION
} from 'app/store/operations/types.js';
import {
    ADD_DIMENSIONS,
    DELETE_DIMENSION,
    MOVE_DIMENSION,
    TOGGLE_COUNT_MEASURE,
    UPDATE_COLUMN,
    UPDATE_VALUES,
    UPDATE_AGGREGATION,
    INITIALIZE_DIMENSIONS,
    INITIALIZE_SOURCE_COLUMNS,
    LOAD_COLUMN_VALUES
} from './types.js';

export const INITIAL_STATE = {
    sourceColumns: [],
    rows: [],
    columns: [],
    measures: [],
    initValues: {
        measures: [],
        rows: [],
        columns: [],
        sourceColumns: []
    },
    isPivot: false,
    error: null,
    isLoadingColumnValues: false
};

const determineNextType = (currentType, nextIndex, listCount, pivotable = false) => {
    if (inRange(0, listCount, nextIndex)) {
        return currentType;
    }

    if (nextIndex >= listCount) {
        return currentType === 'rows' && pivotable ? 'columns' : 'measures';
    }

    return currentType === 'measures' && pivotable ? 'columns' : 'rows';
};

const hasDimension = (dimensionsList, dimension) =>
    dimensionsList.findIndex(({ name }) => name === dimension.name) >= 0;

const sortedValues = (values) => values.slice(0).sort();

const getPossibleAggregation = (columnName, { sourceColumns, isPivot, measures }) => {
    const column = sourceColumns.find((sourceColumn) => sourceColumn.name === columnName);

    if (!isPivot) {
        return isNumericType(column.typeFamily)
            ? AggregationType.SUM
            : AggregationType.COUNT_DISTINCT;
    }

    // Pivot only supports unique aggregations per column
    return head(availableAggregations(column, measures));
};

export const summarizeReducer = produce((draft, action) => {
    switch (action.type) {
        case INITIALIZE_SOURCE_COLUMNS: {
            const { columns, isPivot } = action.payload;
            draft.sourceColumns = columns;
            draft.isPivot = isPivot;

            draft.initValues.sourceColumns = columns;
            draft.initValues.isPivot = isPivot;
            break;
        }

        case INITIALIZE_DIMENSIONS: {
            const { rows, measures, columns = [] } = action.payload;
            draft.rows = rows;
            draft.measures = measures;

            if (draft.isPivot) {
                draft.columns = columns;
                draft.initValues.columns = columns;
            }

            draft.initValues.rows = rows;
            draft.initValues.measures = measures;
            break;
        }

        case TOGGLE_COUNT_MEASURE: {
            const aggregation = AggregationType.COUNT;
            const index = findIndex(
                (m) => !m.name && m.aggregation === aggregation,
                draft.measures
            );

            if (index >= 0) {
                draft.measures.splice(index, 1);
            } else {
                draft.measures.push({ aggregation, values: [] });
            }

            break;
        }

        case ADD_DIMENSIONS: {
            const { type, columns } = action.payload;

            for (const column of columns) {
                // Use the same structure for all dimensions. Depending on the type,
                // only some attributes might be used, but this allows us to preserve
                // attributes when moving a dimension between sections.
                const dimension = {
                    name: column.name,
                    aggregation: getPossibleAggregation(column.name, draft),
                    values: [],
                    allValues: [],
                    pivotable: column.pivotable
                };

                draft[type].push(dimension);
            }

            draft.error = null;
            break;
        }

        case DELETE_DIMENSION: {
            const { type, index } = action.payload;
            draft[type].splice(index, 1);
            draft.error = null;
            break;
        }

        case MOVE_DIMENSION: {
            const { type, index, direction } = action.payload;
            const nextIndex = index + direction;
            const list = draft[type];
            const listCount = list.length;
            const toBeMovedColumn = list[index];
            const pivotable = draft.isPivot && toBeMovedColumn?.pivotable;
            const nextType = determineNextType(type, nextIndex, listCount, pivotable);

            draft.error = null;

            // moving within the same dimension
            if (nextType === type && nextIndex >= 0) {
                const [dimension] = list.splice(index, 1);
                list.splice(nextIndex, 0, dimension);
                break;
            }

            const nextSection = draft[nextType];

            // count dimension is only for measures, so that shouldn't be allowed to move to other sections
            // dimensions other than `measures` don't support duplicate columns
            const canMoveToNextDimension =
                toBeMovedColumn.name &&
                (nextType === 'measures' || !hasDimension(nextSection, toBeMovedColumn, nextType));

            if (!canMoveToNextDimension) {
                break;
            }

            const aggregation = getPossibleAggregation(toBeMovedColumn.name, draft);

            if (isNil(aggregation) && nextType === 'measures') {
                break;
            }

            const [dimension] = list.splice(index, 1);

            const position = direction === 1 ? 0 : draft[nextType].length;
            dimension.aggregation = aggregation;

            draft[nextType].splice(position, 0, dimension);
            break;
        }

        case UPDATE_COLUMN: {
            const { type, index, name } = action.payload;

            draft[type][index].aggregation = getPossibleAggregation(name, draft);
            draft[type][index].name = name;

            if (type === 'columns') {
                draft[type][index].allValues = [];
                draft[type][index].values = [];
            }

            draft.error = null;
            break;
        }

        case UPDATE_VALUES: {
            const { type, index, values } = action.payload;
            draft[type][index].values = sortedValues(values);
            draft.error = null;
            break;
        }

        case UPDATE_AGGREGATION: {
            const { type, index, aggregation } = action.payload;
            draft[type][index].aggregation = aggregation;
            draft.error = null;
            break;
        }
        case CANCEL_OPERATION:
            return INITIAL_STATE;
        case CREATE_TRANSFORMATION.SUCCEEDED:
        case UPDATE_TRANSFORM_OPERATION.SUCCEEDED:
        case ADD_OPERATION_TO_STACK.SUCCEEDED:
            if (action.meta?.keepOpen) {
                draft.initValues.rows = draft.rows.map((r) => ({ ...r }));
                draft.initValues.columns = draft.columns.map((c) => ({ ...c }));
                draft.initValues.measures = draft.measures.map((m) => ({ ...m }));
                draft.error = null;
                break;
            }
            return INITIAL_STATE;
        case LOAD_COLUMN_VALUES.STARTED:
            draft.isLoadingColumnValues = true;
            break;
        case LOAD_COLUMN_VALUES.SUCCEEDED: {
            const {
                payload: { values },
                meta: { columnName }
            } = action;
            const columnIndex = draft.columns.findIndex(({ name }) => name === columnName);

            draft.columns[columnIndex].allValues = values;
            break;
        }
        case LOAD_COLUMN_VALUES.ENDED:
            draft.isLoadingColumnValues = false;
            break;
        default:
            break;
    }
}, INITIAL_STATE);
